import React from 'react';
import {
  Button,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  notification,
} from 'antd';
import styled from 'styled-components';

import theme from 'assets/styles/theme';
import Loading from 'components/common/Loading';
import ScreenMask from 'components/common/ScreenMask';
import { Label } from 'components/common/styles';
import {
  COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
  LS_DEFAULT_WAREHOUSE_KEY,
  LOADING_ICON_SIZE1,
} from 'constants/config';
// import { fetchWarehouseProductList } from 'services/inventory';
import { fetchWarehouseLocationList } from 'services/warehouse';
import { adjustPack } from 'services/prepack';
import { getWarehouseFromCache } from 'utils';

type Props = {
  onClose: Function;
  visible: boolean;
  warehouse: StringKAnyVPair;
};

const AdjustmentWrapper = styled.div`
  margin: 20px 0;

  & .err-msg-ctn,
  & .input-ctn {
    width: 50%;
  }

  & .err-msg-ctn {
    color: ${theme['@danger-color']};
  }

  & .input-prd-qty-ctn .ant-input-number-group-wrapper {
    width: 100%;
  }

  & .input-prd-qty-ctn .ant-input-number-handler-wrap {
    opacity: 1;
  }

  & .label-ctn {
    padding-right: 8px;
    text-align: right;
    width: 40%;
  }
`;

const locationDict: StringKAnyVPair = {};

const PackAdjustmentDialog = (props: Props) => {
  const { useState } = React;
  const [currentFromWarehouse, setCurrentFromWarehouse] = useState<StringKAnyVPair>({});
  const [fromLocation, setFromLocation] = useState<number>();
  //const [fromWarehouseList, setFromWarehouseList] = useState<StringKAnyVPair[]>();
  const [inited, setInited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [itemQty, setItemQty] = useState(1);
  const [toLocationOptions, setToLocationOptions] = useState<any[]>([]);
  const [skuInputVersion, setSkuInputVersion] = useState(0);
  const [toLocation, setToLocation] = useState<number>();
  const [PrepackSKU, setPrepackSKU] = useState<string>('')
  const [unpackAtSrcLocation, setUnpackAtSrcLocation] = useState(true);

  const skuSearchRef = React.useRef<any>(null);

  const adjust = async () => {

      const param = [{
        WarehouseNum: currentFromWarehouse.warehouseNum,
        PrepackSKU: PrepackSKU,
        FromLocationNum: fromLocation,
        ToLocationNum: toLocation,
        PackQty: itemQty,
      }];

      //console.log(param, skuItem);
      setIsLoading(true);

      try {
        const res = await adjustPack(param);

        if (res && res.isSuccess) {
          if(res.data && res.data.length > 0){
            if(res.data[0].Success){
              Modal.confirm({
                cancelText: 'No',
                okText: 'Yes',
                centered: true,
                title: 'Adjusted successfully! Do you want to adjust another?',
                onCancel() {
                  onClose();
                },
                onOk() {
                  setSkuInputVersion(skuInputVersion + 1);
                  clearForm();
                  setTimeout(() => {
                    focusPrepackSkuSearchBox();
                  }, 0);
                },
              });
            } else {
              if (res.data[0].Message) {
                Modal.error({
                  centered: true,
                  title: `Error: ${res.data[0].Message}`,
                });
              }
            }
          }
        } else {
          Modal.error({
            centered: true,
            title: `error: ${res.message}`,
          });
        }
      } catch(e) {
        //console.log('e:', e);
        notification.error({
          message: `Error: ${e}`,
          duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
        });
      } finally {
        setIsLoading(false);
      }
    
  };

  const clearForm = () => {
    //setSku('');
    setItemQty(1);
    setFromLocation(undefined);
    setToLocation(undefined);
  };

  const dialogFooter = () => {
    return (
      <Row justify="end">
        <Space>
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!isUnpackAvailable()}
            onClick={adjust}
            type="primary"
          >
            Submit
          </Button>
        </Space>
      </Row>
    );
  };

  const focusPrepackSkuSearchBox = () => {
    if (skuSearchRef.current) {
      skuSearchRef.current.focus();
    }
  };


  const isUnpackAvailable = () => {
    return fromLocation && toLocation && PrepackSKU && itemQty > 0;
  };

  // eslint-disable-next-line
  const loadInitialData = async () => {
    setIsLoading(true);

    try {
      const whNum = parseInt(
        localStorage.getItem(LS_DEFAULT_WAREHOUSE_KEY) || '-1'
      );
      const wh = getWarehouseFromCache(whNum);

      if (!wh) {
        setIsLoading(false);
        return;
      }

      setCurrentFromWarehouse(wh);

      const locations = locationDict[wh.warehouseNum] ? locationDict[wh.warehouseNum] : await fetchWarehouseLocationList(wh.warehouseId, true);

      if (Array.isArray(locations)) {
        const opts = locations.map(e => {
          return {
            label: e.locationCode,
            value: e.locationNum,
          };
        });

        if (!locationDict[wh.warehouseNum]) {
          locationDict[wh.warehouseNum] = locations;
        }

        setToLocationOptions(opts);
      }
    } finally {
      setIsLoading(false);
      focusPrepackSkuSearchBox();
    }
  };

  const onClose = () => {
    props.onClose();
  };

  const onFromLocationChange = (val: any) => {
    setFromLocation(val);
    if(unpackAtSrcLocation){
      setToLocation(val);
    }
  };

  const onQtyChange = (val: any) => {
    setItemQty(val);
  };

  const onToLocationChange = (val: any) => {
    setToLocation(val);
  };

  const onUnpackSourceLocationChange = (chk: boolean) => {
    setUnpackAtSrcLocation(chk);

    if (chk) {
      setToLocation(fromLocation);
    }
  };

  React.useEffect(() => {
    if (!inited) {
      loadInitialData();
      setInited(true);
    }
  },[inited, loadInitialData]);

  return (<>
    <Modal
      centered
      footer={dialogFooter()}
      onCancel={onClose}
      maskClosable={false}
      title="Pack Adjustment"
      visible={props.visible}
      width={600}
    >
      <AdjustmentWrapper>
        <Space direction="vertical" style={{width: '100%'}}>
          <Row align="middle">
            <div className="label-ctn">
              <Label className="label-bold tail-colon">Warehouse</Label>
            </div>
            <div className="input-ctn">
              <Label className="label-grey">{currentFromWarehouse.warehouseCode}</Label>
            </div>
          </Row>
          <Row align="middle">
            <div className="label-ctn">
              <Label className="label-bold tail-colon">Prepack SKU</Label>
            </div>
            <div className="input-ctn">
              <Input
                allowClear
                key={`sku-input${skuInputVersion}`}
                onChange={(e)=>setPrepackSKU(e.target.value)}

              />
            </div>
          </Row>
          <Row align="middle">
            <div className="label-ctn">
              <Label className="label-bold tail-colon">Qty</Label>
            </div>
            <div className="input-ctn input-prd-qty-ctn">
              <InputNumber
                // addonAfter={<Label className="label-grey">Max({maxAdjustQty()})</Label>}
                onChange={onQtyChange}
                // max={maxAdjustQty()}
                min={1}
                value={itemQty}
              />
            </div>
          </Row>
          <Row align="middle">
            <div className="label-ctn">
              <Label className="label-bold tail-colon">From Location</Label>
            </div>
            <div className="input-ctn">
              <Select
                onChange={onFromLocationChange}
                options={toLocationOptions}
                style={{ width: '100%' }}
                value={fromLocation}
              />
            </div>
          </Row>
          <Row align="middle">
            <div className="label-ctn">
              <Label className="label-bold tail-colon">Pack at Source Location</Label>
            </div>
            <div className="input-ctn">
              <Switch
                checked={unpackAtSrcLocation}
                checkedChildren={'Yes'}
                onChange={onUnpackSourceLocationChange}
                unCheckedChildren={'No'}
              />
            </div>
          </Row>
          <Row align="middle">
            <div className="label-ctn">
              <Label className="label-bold tail-colon">To Location</Label>
            </div>
            <div className="input-ctn">
              <Select
                disabled={unpackAtSrcLocation}
                filterOption={(value: string, option: any)=> {return option.label.toLowerCase().indexOf(value.toLowerCase()) >= 0}}
                onChange={onToLocationChange}
                options={toLocationOptions}
                showSearch
                style={{ width: '100%' }}
                value={toLocation}
              />
            </div>
          </Row>
        </Space>
      </AdjustmentWrapper>
    </Modal>
    {isLoading && (
      <ScreenMask>
        <Loading size={LOADING_ICON_SIZE1} />
      </ScreenMask>
    )}
  </>);
};

export default PackAdjustmentDialog;
